"use client";

import {
  COOKIE_KEY_HLW_CONSENT,
  deleteCookie,
  SESSION_STORAGE_KEY_GOOGLE_CLICK_IDS,
  setCookie,
  useCookie,
  useRequestGeolocation,
  useRequestMe,
  useSession,
} from "@packages/sdk";
import type { ComponentProps } from "react";
import { useEffect, useState } from "react";
import { z } from "zod";

import { DEFAULT_CONSENT_OPTIONS_STRICT } from "../constants/defaultConsentOptionsStrict.constants";
import type { ConsentContext } from "../lib/consentContext";
import { consentContext } from "../lib/consentContext";
import { Consent } from "../types/enums/consent.enum";
import { handleConsentFromGeolocation } from "../utils/handleConsentFromGeolocation";

const GOOGLE_CLICK_ID_PARAM_KEYS = ["gclid", "wbraid", "gbraid"] as const;

export type ConsentProviderProps = Omit<
  ComponentProps<typeof consentContext.Provider>,
  "value"
>;

export const ConsentProvider = ({
  children,
  ...props
}: ConsentProviderProps) => {
  const [consent, setConsent] = useCookie<number[]>({
    key: COOKIE_KEY_HLW_CONSENT,
    schema: z.number().array(),
  });
  const { query: queryGeolocation } = useRequestGeolocation();
  const { mutationPut: mutationPutMe, query: queryMe } = useRequestMe();
  const { status } = useSession();
  const [isError, setIsError] = useState(false);

  const isConsenting =
    Array.isArray(consent) && consent.includes(Consent.ANALYTICS);
  const isLoading = !consent;

  /** TODO: remove/improve when ConsentModal/CookieManager are improved */
  const [isConsentModalVisible, setIsConsentModalVisible] = useState(false);
  const saveConsent = async ({
    consent,
  }: {
    consent: ConsentContext["consent"];
  }) => {
    if (status === "authenticated") {
      await mutationPutMe.mutateAsync({ consent });
    }
    setConsent(consent);
  };

  /**
   * If the user does not have consent preferences
   */
  useEffect(() => {
    if (
      (!consent ||
        !(
          Array.isArray(consent) &&
          (consent?.length === 0 ||
            consent?.every((c) => typeof c === "number"))
        )) &&
      !queryGeolocation.isLoading
    ) {
      handleConsentFromGeolocation({
        geolocation: queryGeolocation.data,
        setConsent,
        setIsConsentModalVisible,
      });

      let gpc = false;
      if (typeof navigator !== "undefined") {
        // @ts-ignore -- this is what happens when you implement things that aren't really standards
        gpc = !!navigator.globalPrivacyControl;
      }

      if (gpc) setConsent(DEFAULT_CONSENT_OPTIONS_STRICT);
    }
  }, [consent, queryGeolocation.data, queryGeolocation.isLoading]);

  /** Sync any inconsistent consents */
  useEffect(() => {
    if (queryMe.data?.consent) {
      if (
        (consent &&
          JSON.stringify(consent?.sort()) !==
            JSON.stringify(queryMe.data.consent.sort())) ||
        !consent
      ) {
        setConsent(queryMe.data.consent);
      }
    } else if (consent && status === "authenticated") {
      mutationPutMe.mutate({ consent });
    }
  }, [consent, queryMe.data?.consent]);

  useEffect(() => {
    if (!Array.isArray(consent)) return;

    const hasAdvertisingConsent = consent.includes(Consent.ADVERTISING);
    const urlParams = new URLSearchParams(window.location.search);
    const storedClickIds = JSON.parse(
      sessionStorage.getItem(SESSION_STORAGE_KEY_GOOGLE_CLICK_IDS) ?? "{}",
    ) as Partial<Record<(typeof GOOGLE_CLICK_ID_PARAM_KEYS)[number], string>>;

    const clickIds = GOOGLE_CLICK_ID_PARAM_KEYS.reduce<
      Partial<Record<(typeof GOOGLE_CLICK_ID_PARAM_KEYS)[number], string>>
    >((acc, key) => {
      const value = urlParams.get(key) ?? storedClickIds[key];

      if (value) acc[key] = value;

      return acc;
    }, {});

    sessionStorage.setItem(
      SESSION_STORAGE_KEY_GOOGLE_CLICK_IDS,
      JSON.stringify(clickIds),
    );

    if (!hasAdvertisingConsent) {
      GOOGLE_CLICK_ID_PARAM_KEYS.forEach((key) => {
        deleteCookie({ key, options: { domain: ".hallow.com", path: "/" } });
      });
      return;
    }

    GOOGLE_CLICK_ID_PARAM_KEYS.forEach((key) => {
      const value = clickIds[key];

      if (!value) return;

      setCookie({
        key,
        domain: ".hallow.com",
        options: { expires: 90, sameSite: "lax" },
        value,
      });
    });
  }, [consent]);

  return (
    <consentContext.Provider
      value={{
        consent,
        isConsenting,
        isLoading,
        isError,
        isConsentModalVisible,
        saveConsent,
        setConsent,
        setIsError,
        setIsConsentModalVisible,
      }}
      {...props}
    >
      {children}
    </consentContext.Provider>
  );
};
